import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Play, Zap, Clock, IndianRupee } from 'lucide-react';
import { Button } from '../ui/button';

const Hero = () => {
  const whatsappLink = "https://wa.me/919353316112?text=Hi%20LitheForce!%20I'm%20interested%20in%20your%20AI%20solutions.";

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 hero-gradient overflow-hidden"
      aria-label="Hero"
      data-testid="hero-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#00e5ff]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#1a237e]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#00e5ff]/5 to-[#1a237e]/5 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a237e]/5 border border-[#1a237e]/10 mb-6"
          >
            <Zap className="w-4 h-4 text-[#ff6d00]" />
            <span className="text-sm font-medium text-[#1a237e]">AI-First Software Agency</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#212121] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-headline"
          >
            Build AI Chatbots & Agents{' '}
            <span className="gradient-text">10x Faster</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-[#5a6b8c] max-w-2xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subheadline"
          >
            We deliver custom AI solutions in weeks, not months—at{' '}
            <span className="font-semibold text-[#1a237e]">5x lower cost</span> than traditional agencies
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
          >
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button
                className="btn-primary text-lg px-8 py-4 h-auto flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                <MessageCircle className="w-5 h-5" />
                Book Free Consultation
              </Button>
            </a>
            <a href="#demo">
              <Button
                variant="outline"
                className="btn-secondary text-lg px-8 py-4 h-auto flex items-center gap-2"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </Button>
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-3 gap-4 md:gap-8 max-w-xl mx-auto"
          >
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-2xl md:text-3xl font-bold text-[#1a237e]">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-[#00e5ff]" />
                2-4
              </div>
              <p className="text-sm text-[#5a6b8c] mt-1">Weeks Delivery</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-2xl md:text-3xl font-bold text-[#1a237e]">
                <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-[#00e5ff]" />
                50K
              </div>
              <p className="text-sm text-[#5a6b8c] mt-1">Starting Price</p>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-[#1a237e]">90%</div>
              <p className="text-sm text-[#5a6b8c] mt-1">AI-Powered</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-[#1a237e]/20 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1.5 h-1.5 rounded-full bg-[#1a237e]/40"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
